import BigNumber from 'bignumber.js'
import erc20ABI from 'config/abi/erc20.json'
import craftsmanABI from 'config/abi/craftsman.json'
import craftsmanV2ABI from 'config/abi/craftsmanV2.json'
import multicall from 'utils/multicall'
import { getAddress, getCraftsmanAddress, getCraftsmanV2Address } from 'utils/addressHelpers'
import { SerializedFarmConfig } from 'config/constants/types'
import { getVvsBoostFarmContract } from 'utils/contractHelpers'
import { BigNumber as EthersBigNumber, Signer } from 'ethers'

export const fetchFarmUserAllowances = async (account: string, farmsToFetch: SerializedFarmConfig[]) => {
  const craftsmanAddress = getCraftsmanAddress()
  const craftsmanV2Address = getCraftsmanV2Address()

  const calls = farmsToFetch.map((farm) => {
    const lpContractAddress = getAddress(farm.lpAddresses)
    if (farm.version === 2) {
      return { address: lpContractAddress, name: 'allowance', params: [account, craftsmanV2Address] }
    }
    return { address: lpContractAddress, name: 'allowance', params: [account, craftsmanAddress] }
  })

  const rawLpAllowances = await multicall(erc20ABI, calls)
  const parsedLpAllowances = rawLpAllowances.map((lpBalance) => {
    return new BigNumber(lpBalance).toJSON()
  })
  return parsedLpAllowances
}

export const fetchFarmUserTokenBalances = async (account: string, farmsToFetch: SerializedFarmConfig[]) => {
  const calls = farmsToFetch.map((farm) => {
    const lpContractAddress = getAddress(farm.lpAddresses)
    return {
      address: lpContractAddress,
      name: 'balanceOf',
      params: [account],
    }
  })

  const rawTokenBalances = await multicall(erc20ABI, calls)
  const parsedTokenBalances = rawTokenBalances.map((tokenBalance) => {
    return new BigNumber(tokenBalance).toJSON()
  })
  return parsedTokenBalances
}

export const fetchFarmUserStakedBalances = async (account: string, farmsToFetch: SerializedFarmConfig[]) => {
  const craftsmanAddress = getCraftsmanAddress()

  const calls = farmsToFetch.map((farm) => {
    return {
      address: craftsmanAddress,
      name: 'userInfo',
      params: [farm.pid, account],
    }
  })

  const rawStakedBalances = await multicall(craftsmanABI, calls)
  const parsedStakedBalances = rawStakedBalances.map((stakedBalance) => {
    return new BigNumber(stakedBalance[0]._hex).toJSON()
  })
  return parsedStakedBalances
}

export const fetchFarmUserStakedBalancesV2 = async (account: string, farmsToFetch: SerializedFarmConfig[]) => {
  const craftsmanV2Address = getCraftsmanV2Address()

  const calls = farmsToFetch.map((farm) => {
    return {
      address: craftsmanV2Address,
      name: 'userInfo',
      params: [farm.pid, account],
    }
  })

  const rawStakedBalances = await multicall(craftsmanV2ABI, calls)
  const parsedStakedBalances = rawStakedBalances.map((stakedBalance) => {
    return new BigNumber(stakedBalance[0]._hex).toJSON()
  })
  return parsedStakedBalances
}

export const fetchFarmUserEarnings = async (account: string, farmsToFetch: SerializedFarmConfig[]) => {
  const craftsmanAddress = getCraftsmanAddress()

  const calls = farmsToFetch.map((farm) => {
    return {
      address: craftsmanAddress,
      name: 'pendingVVS',
      params: [farm.pid, account],
    }
  })

  const rawEarnings = await multicall(craftsmanABI, calls)
  // const rawEarnings = calls.map(() => 0); // FIXME debug hard code for warning
  const parsedEarnings = rawEarnings.map((earnings) => {
    return new BigNumber(earnings).toJSON()
  })
  return parsedEarnings
}

export const fetchFarmUserEarningsV2 = async (account: string, farmsToFetch: SerializedFarmConfig[]) => {
  const craftsmanV2Address = getCraftsmanV2Address()

  const calls = farmsToFetch.map((farm) => {
    return {
      address: craftsmanV2Address,
      name: 'pendingVVS',
      params: [farm.pid, account],
    }
  })

  const rawEarnings = await multicall(craftsmanV2ABI, calls)
  // const rawEarnings = calls.map(() => 0); // FIXME debug hard code for warning
  const parsedEarnings = rawEarnings.map((earnings) => {
    return new BigNumber(earnings).toJSON()
  })
  return parsedEarnings
}

export const fetchFarmUserPendingRewardsV2 = async (account: string, farmsToFetch: SerializedFarmConfig[]) => {
  const craftsmanV2Address = getCraftsmanV2Address()

  const calls = farmsToFetch.map((farm) => {
    return {
      address: craftsmanV2Address,
      name: 'pendingTokens',
      params: [farm.pid, account],
    }
  })

  const rawEarnings = await multicall(craftsmanV2ABI, calls)
  const parsedEarnings = rawEarnings.map(([tokenAddrs, earnings]) => {
    return { tokens: tokenAddrs, earnings: earnings.map((earning) => new BigNumber(earning._hex).toJSON()) }
  })
  return parsedEarnings
}

export const fetchFarmUserPendingBoostRewards = async (farmsToFetch: SerializedFarmConfig[], signer?: Signer) => {
  const vvsFarmBoostContract = getVvsBoostFarmContract(signer)
  const pendingBoostRewards = await Promise.all(
    farmsToFetch.map((farm) =>
      farm.enableBoost
        ? vvsFarmBoostContract.callStatic['harvestBoost(uint256)'](farm.pid)
        : Promise.resolve(EthersBigNumber.from('0')),
    ),
  )
  const results = pendingBoostRewards.map((res) => new BigNumber(res?._hex).toJSON())
  return results
}

export const fetchFarmUserPendingBoostRewardsAllFarms = async (signer?: Signer) => {
  const vvsFarmBoostContract = getVvsBoostFarmContract(signer)
  const pendingBoostRewardsAllFarms = await vvsFarmBoostContract.callStatic['withdrawBoostReward()']()
  return new BigNumber(pendingBoostRewardsAllFarms?._hex).toJSON()
}
